import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography,
  Row,
  Col,
  message,
  Empty,
  Space,
  Tooltip,
  Badge,
  Skeleton,
  Select
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  RightOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { TournamentService } from '@/services/tournament.service';
import { formatDate } from '@/utils/date';
import { StatusTag } from './StatusTag';
import { logger } from '@/utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { CreateTournamentModal } from '../admin/CreateTournamentModal';
import { getUploadsBaseUrl, normalizeUploadImageUrl } from '@/config/api.config';
import { TournamentCard, CreateButton, StyledEmpty, StyledTabs } from './styles/TournamentsPage.styles';

// Используем интерфейс из api.types
import type { Tournament } from '@/types/api.types';

const { Title, Text } = Typography;

const TournamentCardSkeleton: React.FC = () => (
  <TournamentCard>
    <Skeleton active paragraph={{ rows: 4 }} />
  </TournamentCard>
);

const TournamentsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('upcoming');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [regions, setRegions] = useState<string[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Проверяем, может ли пользователь создавать турниры (админ, менеджер, руководитель, менеджер клуба)
  const canCreateTournament = user && ['admin', 'manager', 'head_manager'].includes(user.role);

  // Проверяем, может ли пользователь видеть черновики (менеджер или тренер клуба)
  const canViewDrafts = user && ['admin', 'manager', 'coach', 'head_manager'].includes(user.role) && user.clubId;

  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const regionsList = await TournamentService.getRegions();
      setRegions(regionsList);
    } catch (error) {
      logger.error('Error fetching regions:', error);
      // Не показываем ошибку пользователю, просто оставляем список пустым
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const filter: any = {};
      if (selectedRegion) {
        filter.region = selectedRegion;
      }
      const data = await TournamentService.getAll(filter);
      setTournaments(data);
    } catch (error) {
      logger.error('Error fetching tournaments:', error);
      message.error('Не удалось загрузить список турниров');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  // Загружаем и обновляем турниры при открытии/переходе на страницу (в т.ч. после создания турнира)
  useEffect(() => {
    if (location.pathname === '/tournaments') {
      fetchTournaments();
    }
  }, [location.pathname]);

  useEffect(() => {
    // Перезагружаем турниры при изменении фильтра по региону
    fetchTournaments();
  }, [selectedRegion]);

  // Определяем черновики для текущего пользователя
  const draftTournaments = useMemo(() => {
    if (!canViewDrafts) return [];
    return tournaments.filter(t => 
      t.status === 'draft' && (t as any).clubId === user?.clubId
    );
  }, [tournaments, canViewDrafts, user?.clubId]);

  // Фильтруем турниры по вкладкам
  const filteredTournaments = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case 'upcoming': {
        // Предстоящие: дата турнира >= сегодня и статус не 'draft' и не 'completed'
        return tournaments.filter(t => {
          const tournamentDate = new Date(t.tournamentDate);
          tournamentDate.setHours(0, 0, 0, 0);
          return tournamentDate >= now && 
                 t.status !== 'draft' && 
                 t.status !== 'completed' &&
                 t.status !== 'deleted';
        });
      }
      case 'past': {
        // Прошедшие: дата турнира < сегодня или статус 'completed'
        return tournaments.filter(t => {
          const tournamentDate = new Date(t.tournamentDate);
          tournamentDate.setHours(0, 0, 0, 0);
          return (tournamentDate < now || t.status === 'completed') && 
                 t.status !== 'draft' &&
                 t.status !== 'deleted';
        });
      }
      case 'drafts': {
        // Черновики: статус 'draft' и clubId совпадает с clubId пользователя
        return draftTournaments;
      }
      default:
        return tournaments;
    }
  }, [tournaments, activeTab, draftTournaments]);

  const [isCreateTournamentModalVisible, setIsCreateTournamentModalVisible] = useState(false);
  const [checkingLicense, setCheckingLicense] = useState(false);

  const handleCreateTournament = async () => {
    if (user?.role === 'admin') {
      setIsCreateTournamentModalVisible(true);
      return;
    }
    try {
      setCheckingLicense(true);
      const res = await apiService.get<ApiResponse<{ canCreateTournament: boolean; extendedLicensePrice?: number }>>(
        API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
      );
      const data = res.data?.data;
      if (data && !data.canCreateTournament) {
        navigate('/extended-license', { state: { fromCreate: true, extendedLicensePrice: data.extendedLicensePrice } });
        return;
      }
      setIsCreateTournamentModalVisible(true);
    } catch (e) {
      logger.error('Error checking extended license:', e);
      message.error('Не удалось проверить возможность создания турнира');
    } finally {
      setCheckingLicense(false);
    }
  };

  const handleTournamentCreateSuccess = () => {
    setIsCreateTournamentModalVisible(false);
    // Перезагружаем список турниров
    fetchTournaments();
  };

  const handleTournamentClick = (id: number) => {
    navigate(`/tournaments/${id}`, {
      state: { activeTab: activeTab }
    });
  };

  const renderTournamentCards = () => {
    if (loading) {
      return Array(8).fill(null).map((_, index) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={index}>
          <TournamentCardSkeleton />
        </Col>
      ));
    }

    if (filteredTournaments.length === 0) {
      return (
        <Col span={24}>
          <StyledEmpty
            image={Empty.PRESENTED_IMAGE_DEFAULT}
            description={
              <Space direction="vertical" size="large" align="center">
                <Text>Турниры не найдены</Text>
                {canCreateTournament && (
                  <CreateButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateTournament}
                    loading={checkingLicense}
                  >
                    Создать турнир
                  </CreateButton>
                )}
              </Space>
            }
          />
        </Col>
       );
    }

    return filteredTournaments.map(tournament => (
      <Col xs={24} sm={12} lg={8} xl={6} key={tournament.id}>
        <TournamentCard
          onClick={() => handleTournamentClick(tournament.id)}
          hoverable
          cover={
            tournament.coverImage && (() => {
              const raw = tournament.coverImage.startsWith('http')
                ? tournament.coverImage
                : (() => {
                    const baseUrl = getUploadsBaseUrl();
                    const imagePath = tournament.coverImage!.startsWith('/') ? tournament.coverImage! : '/' + tournament.coverImage;
                    return `${baseUrl}${imagePath}`;
                  })();
              const imageUrl = normalizeUploadImageUrl(raw);
              return imageUrl ? (
                <img
                  alt={tournament.name}
                  src={imageUrl}
                  style={{ height: 200, objectFit: 'contain' }}
                />
              ) : null;
            })()
          }
        >
          <Title level={4} className="tournament-title">
            {tournament.name}
          </Title>

          <div className="tournament-info">
            <CalendarOutlined />
            <Text>{formatDate(tournament.tournamentDate)}</Text>
          </div>

          <div className="tournament-info">
            <EnvironmentOutlined />
            <Tooltip title={tournament.location}>
              <Text ellipsis style={{ maxWidth: '100%' }}>
                {tournament.location}
              </Text>
            </Tooltip>
          </div>

          <div className="tournament-info">
            <TeamOutlined />
            <Text>
              {tournament.participantsCount || 0} участников
            </Text>
          </div>

          {tournament.registrationDeadline && (
            <div className="tournament-info">
              <ClockCircleOutlined />
              <Text type="secondary">
                Регистрация до {formatDate(tournament.registrationDeadline)}
              </Text>
            </div>
          )}

          <div className="tournament-status">
            <StatusTag status={tournament.status} />
            <Badge
              count={tournament.categories?.length || 0}
              overflowCount={99}
              style={{ backgroundColor: '#52c41a' }}
              title="Количество категорий"
            />
          </div>

          <div className="tournament-footer">
            <RightOutlined style={{ color: 'var(--primary-color, #1890ff)' }} />
          </div>
        </TournamentCard>
      </Col>
    ));
  };

  // Определяем, показывать ли вкладку черновиков
  const hasDrafts = draftTournaments.length > 0;

  const tabItems: Array<{ key: string; label: React.ReactNode; children: React.ReactNode }> = [
    {
      key: 'upcoming',
      label: 'ПРЕДСТОЯЩИЕ ТУРНИРЫ',
      children: (
        <Row gutter={[24, 24]}>
          {renderTournamentCards()}
        </Row>
      )
    },
    {
      key: 'past',
      label: 'ПРОШЕДШИЕ ТУРНИРЫ',
      children: (
        <Row gutter={[24, 24]}>
          {renderTournamentCards()}
        </Row>
      )
    }
  ];

  // Добавляем вкладку черновиков, если есть черновики
  if (hasDrafts) {
    tabItems.push({
      key: 'drafts',
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          ЧЕРНОВИКИ
          {filteredTournaments.length > 0 && activeTab === 'drafts' && (
            <Badge count={filteredTournaments.length} style={{ marginLeft: 8 }} />
          )}
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {renderTournamentCards()}
        </Row>
      )
    });
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <Title level={2} style={{ margin: 0 }}>Турниры</Title>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {regions.length > 0 && (
            <Select
              placeholder="Фильтр по региону"
              allowClear
              style={{ minWidth: 200 }}
              value={selectedRegion}
              onChange={(value) => setSelectedRegion(value || null)}
              loading={loadingRegions}
              options={regions.map(region => ({
                label: region,
                value: region
              }))}
            />
          )}
          {canCreateTournament && (
            <CreateButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateTournament}
              loading={checkingLicense}
            >
              Создать турнир
            </CreateButton>
          )}
        </div>
      </div>

      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      {/* Модальное окно создания турнира */}
      {canCreateTournament && (
        <CreateTournamentModal
          visible={isCreateTournamentModalVisible}
          onCancel={() => setIsCreateTournamentModalVisible(false)}
          onSuccess={handleTournamentCreateSuccess}
        />
      )}
    </div>
  );
};

export default TournamentsPage;